import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'use-context-selector';

import { API_ENDPOINT_KEYS, SERVICE_AUTH_ENDPOINTS } from '~common';
import { AUTH_API_CLIENT_ID, LOGIN_PATH } from '~configs';
import {
  LOCAL_CONTACT_SKIPPED_STEP,
  LOCAL_FORM_VALUES,
  LOCAL_GOOGLE_PLACE_ID,
  LOCAL_PINNING_STEP,
  LOCAL_PLACEMENT_LIST,
  LOCAL_VENUE_ID,
} from '~providers';
import { postAuthRequest } from '~services/auth.service';
import { inMemoryJWTService, USER_LOGOUT_EVENT } from '~services/jwt.service';

interface AuthContextType {
  signIn: (credential: Record<string, unknown>) => Promise<void>;
  signOut: () => void;
  clearUserInfo: () => void;
}

const AuthContext = createContext<AuthContextType>({
  signIn: () => Promise.resolve(),
  signOut: () => null,
  clearUserInfo: () => null,
});

type Props = {
  children: React.ReactNode;
};

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signIn = async (credential: Record<string, unknown>) => {
    try {
      const { data } = await postAuthRequest({
        path: SERVICE_AUTH_ENDPOINTS[API_ENDPOINT_KEYS.LOGIN],
        data: {
          username: credential.email,
          password: credential.password,
          clientId: AUTH_API_CLIENT_ID,
        },
        ignoreAuthHeader: true,
      });
      const { accessToken, refreshToken, expiresIn } = data;
      inMemoryJWTService.setToken(accessToken, expiresIn);
      inMemoryJWTService.setRefreshToken(refreshToken);

      queryClient.resetQueries();
      window.localStorage.removeItem(USER_LOGOUT_EVENT);
      //Clear step data when user login
      window.localStorage.removeItem(LOCAL_PINNING_STEP);
      window.localStorage.removeItem(LOCAL_VENUE_ID);
      window.localStorage.removeItem(LOCAL_FORM_VALUES);
      window.localStorage.removeItem(LOCAL_PLACEMENT_LIST);
      window.localStorage.removeItem(LOCAL_GOOGLE_PLACE_ID);
      window.localStorage.removeItem(LOCAL_CONTACT_SKIPPED_STEP);
      navigate('/', { replace: true });
    } catch (err) {
      throw err;
    }
  };

  const clearUserInfo = () => {
    inMemoryJWTService.eraseToken();
    sessionStorage.clear();
  };

  const signOut = () => {
    clearUserInfo();
    navigate(LOGIN_PATH, { replace: true });
  };

  useEffect(() => {
    // This is to help logout when 2 browsers login as the same time
    // The first to login will logout
    const handleLogoutEvent = () => {
      navigate(LOGIN_PATH, { replace: true });
    };

    // This is to help logout when more than 2 tabs open
    // One of the tab logout, which will make other logout too
    const handleLogoutEventWithStorage = (e: StorageEvent) => {
      if (
        e.key === USER_LOGOUT_EVENT &&
        localStorage.getItem(USER_LOGOUT_EVENT)
      )
        navigate(LOGIN_PATH, { replace: true });
    };

    window.addEventListener('logoutEvent', handleLogoutEvent);
    window.addEventListener('storage', handleLogoutEventWithStorage);

    return () => {
      window.removeEventListener('logoutEvent', handleLogoutEvent);
      window.removeEventListener('storage', handleLogoutEventWithStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = { signIn, signOut, clearUserInfo };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

type RequireAuthProps = {
  children: JSX.Element;
};

export const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  if (!inMemoryJWTService.getCurrentToken()) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" replace={true} />;
  }

  return children;
};
